import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  DialogActions,
  Button,
  Switch,
  Alert,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Radio,
  Chip,
  IconButton,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import {
  NotificationStage,
  NotificationTemplate,
  OrganisationSettings,
} from '../../../../interfaces/delivery-company/orders';
import { timestampedMessage } from '../../../common/multi-package-scanner/multi-package-scanner';
import axios, { AxiosResponse } from 'axios';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import {
  resolveTemplate,
  IResolveTemplateArgs,
} from '../../../delivery-company-components/settings/notification-manager/utils';
import { Box, Paper, Typography } from '@mui/material';
import { useSnackBar } from '../../../../contexts/snackbar';
import { Clear, Done, HourglassBottomOutlined, Phone, WhatsApp } from '@mui/icons-material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { grey } from '@mui/material/colors';
import { IconWithText } from '../../../common/styles/components';

interface ILocallyResolvedTarget {
  id: string;
  label: string;
  phone_number: string;
  resolveTemplateArgs: IResolveTemplateArgs;
  alreadyStarted: boolean;
  disabled?: boolean;
}

interface SendMessageDialogProps {
  onSubmit: (template: string) => Promise<AxiosResponse>;
  onClose: () => void;
  availableTemplates?: NotificationTemplate[];
  stage: NotificationStage;
  setSnackBarResult: (t?: timestampedMessage) => void;
  onSuccess: () => void;
  locallyResolvedTargets: ILocallyResolvedTarget[]; // list of targets locally resolved. Useful to send messages locally via whatsapp application
  startTask: (id: string) => Promise<boolean>;
  openInWhatsapp?: boolean;
}

function SendMessageDialog({
  onSubmit,
  onClose,
  availableTemplates,
  setSnackBarResult,
  onSuccess,
  stage,
  locallyResolvedTargets,
  startTask,
}: SendMessageDialogProps) {
  const [notificationTemplate, setNotificationTemplate] = useState<string>();
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user, refreshUser, config } = useContext(AuthContext);
  const [sentTargetIDs, setSentTargetIDs] = useState(new Set<string>());
  const addTargetTargetIDToSet = (id: string) => {
    const newSet = new Set(sentTargetIDs);
    newSet.add(id);
    setSentTargetIDs(newSet);
  };

  const [doLocalSend, setDoLocalSend] = useState<boolean>(false);

  const [orgTemplates, setOrgTemplates] = useState<NotificationTemplate[]>();
  const [isLoadingStartTask, setIsLoadingStartTask] = useState<string | null>(
    null
  );
  const { showMessage } = useSnackBar();

  console.log('available messages = ', availableTemplates);

  useEffect(() => {
    setNotificationTemplate('');
  }, [useCustomMessage]);

  const getWhatsAppLink = (phoneNumber: string, message: string) => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phoneNumber.replaceAll(' ', '')}?text=${encodedMessage}`;
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/settings`;
        const response = await axios.get<OrganisationSettings>(
          endpoint,
          config
        );
        const orgTemplates = response.data.notification_templates.filter(
          (item) => item.orderWithinStages != undefined
        );
        setOrgTemplates(orgTemplates);

        switch (stage) {
          case 'Pickup': {
            setOrgTemplates(
              orgTemplates
                .filter((item) => item.orderWithinStages.Pickup != undefined)
                .sort((a, b) =>
                  a.orderWithinStages.Pickup! < b.orderWithinStages.Pickup!
                    ? -1
                    : 1
                )
            );
            break;
          }
          case 'Delivery': {
            setOrgTemplates(
              orgTemplates
                .filter((item) => item.orderWithinStages.Delivery != undefined)
                .sort((a, b) =>
                  a.orderWithinStages.Delivery! < b.orderWithinStages.Delivery!
                    ? -1
                    : 1
                )
            );
            break;
          }
          case 'Payment': {
            setOrgTemplates(
              orgTemplates
                .filter((item) => item.orderWithinStages.Payment != undefined)
                .sort((a, b) =>
                  a.orderWithinStages.Payment! < b.orderWithinStages.Payment!
                    ? -1
                    : 1
                )
            );
            break;
          }
        }
      } catch (e) {
        setSnackBarResult({
          timestamp: Date.now(),
          content: 'Failed to load notification templates',
          level: 'error',
        });
      }
    };

    fetchTemplates();
    refreshUser();
  }, []);

  const handleSubmit = async () => {
    setSnackBarResult(undefined);
    setResponse(undefined);
    setIsLoading(true);
    if (!notificationTemplate) {
      setSnackBarResult({
        content: 'Message has no content',
        level: 'error',
        timestamp: Date.now(),
      });
      setIsLoading(false);
      return;
    }
    try {
      setSnackBarResult(undefined);
      const response = await onSubmit(notificationTemplate);
      setResponse(response);
      setSnackBarResult({
        content: 'Customers Notified',
        level: 'success',
        timestamp: Date.now(),
      });
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      const msg = 'Failed to send some (or all) messages';
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
      }
      setMessage(msg);
      setSnackBarResult({
        content: msg,
        level: 'error',
        timestamp: Date.now(),
      });
    }

    setIsLoading(false);
  };

  return (
    <CustomDialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle> Notify Customers</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <>
            Sending messages 🚀... <br /> <CircularProgress />
          </>
        ) : (
          <>
            {message != '' && (
              <>
                <Alert
                  severity={
                    response && response.status >= 200 && response.status < 300
                      ? 'success'
                      : 'error'
                  }
                >
                  <>
                    {message}

                    {response && response.status >= 300 && (
                      <ErrorsFlattenner rawErrors={response.data} />
                    )}
                  </>
                </Alert>
                <br />
              </>
            )}
            {!useCustomMessage && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Choose template</InputLabel>
                  <Select
                    value={notificationTemplate}
                    onChange={(e) => setNotificationTemplate(e.target.value)}
                    label="Choose message template"
                  >
                    {orgTemplates?.map((tmpl) => (
                      <MenuItem value={tmpl.template} key={tmpl.name}>
                        {tmpl.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {notificationTemplate && (
                  <TextField
                    autoComplete="off"
                    fullWidth
                    value={notificationTemplate}
                    onChange={(e) => setNotificationTemplate(e.target.value)}
                    margin="normal"
                    label="Custom Message"
                    multiline
                    disabled={true}
                    variant="outlined"
                  />
                )}
              </>
            )}
            <br />
            <Switch
              checked={useCustomMessage}
              onChange={() => {
                setUseCustomMessage(!useCustomMessage);
              }}
            />{' '}
            Write my message.
            <br />
            {useCustomMessage && (
              <TextField
                autoComplete="off"
                fullWidth
                value={notificationTemplate}
                onChange={(e) => setNotificationTemplate(e.target.value)}
                margin="normal"
                label="Custom Message"
                multiline
                rows={4}
                variant="outlined"
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          disabled={
            isLoading ||
            notificationTemplate == undefined ||
            notificationTemplate.length == 0
          }
          onClick={() => setDoLocalSend(true)}
        >
          Manual Send
        </Button>

        {user?.connected_to_whatsapp && (
          <Button
            size="small"
            onClick={handleSubmit}
            disabled={
              isLoading ||
              notificationTemplate == undefined ||
              notificationTemplate.length == 0
            }
            color="primary"
          >
            Send All
          </Button>
        )}
      </DialogActions>

      {doLocalSend && (
        <CustomDialog
          open={doLocalSend}
          onClose={() => setDoLocalSend(false)}
          fullScreen
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            Notify Customer & Start Tasks
          </DialogTitle>
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
            }}
          >
            <IconButton onClick={() => setDoLocalSend(false)}>
              <Clear color="primary" fontSize="large" />
            </IconButton>
          </div>
          <DialogContent sx={{ paddingX: 0 }}>
            {locallyResolvedTargets?.map((item, index) => (
              <Box key={index} sx={{
                paddingY: 1,
                paddingX: 1,
                backgroundColor: index % 2 == 1 ? 'white' : grey[200],
                borderBottom:
                  index !== locallyResolvedTargets.length - 1
                    ? '1px solid #e0e0e0'
                    : 'none',
                '&:hover': {
                  backgroundColor: '#f9f9f9',
                },
              }}>
              <Grid2
                spacing={2}
                container
                key={item.id}
              >
                <Grid2 xs={4} md={4} sx={{ textAlign: 'center' }}>
                  <div>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="body2">
                        <b>{item.resolveTemplateArgs.recipient_name}</b>
                      </Typography>
                      <Typography variant="body2">
                        <b>{item.phone_number}</b>
                      </Typography>
                      <Typography variant="body2">{item.label}</Typography>
                    </Box>
                  </div>
                </Grid2>
                
                <Grid2
                  xs={4}
                  md={4}
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    color={sentTargetIDs.has(item.id) ? 'success' : 'primary'}
                    disabled={item.disabled || isLoadingStartTask != null}
                    onClick={async () => {
                      if (!item.alreadyStarted) {
                        setIsLoadingStartTask(item.id);
                        if (!(await startTask!(item.id))) {
                          showMessage('Failed to start task', 'error');
                          setIsLoadingStartTask(null);
                          return;
                        }
                        setIsLoadingStartTask(null);
                      }

                      addTargetTargetIDToSet(item.id);
                      const message = resolveTemplate(
                        notificationTemplate ?? '',
                        item.resolveTemplateArgs
                      );
                      const WALink = getWhatsAppLink(
                        item.phone_number,
                        message
                      );
                      window.open(WALink, '_blank');
                    }}
                  >
                    Send{' '}
                    {sentTargetIDs.has(item.id) && (
                      <Done fontSize="small" sx={{ ml: 1 }} />
                    )}
                    <br />
                    {isLoadingStartTask == item.id && (
                      <CircularProgress size={20} />
                    )}
                  </Button>
                </Grid2>

                <Grid2
                  xs={4}
                  md={4}
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  {item.alreadyStarted ? (
                    <Chip
                      size="small"
                      color="success"
                      icon={<Done />}
                      label="Started"
                    />
                  ) : (
                    <Chip
                      size="small"
                      color="secondary"
                      icon={<HourglassBottomOutlined />}
                      label="Pending"
                    />
                  )}
                </Grid2>

              </Grid2>
              
                {item.disabled && <Box mt={2}><Typography align={"center"} variant="body2" color={"error"} fontWeight={"bold"}>
                    Notifications are disabled for this customer. <br/>
                    For urgent topics, contact customer:

                </Typography>
                  <Box width={"100%"} justifyContent={"center"} display={"flex"}>
                    <IconButton  onClick={()=>{
                      const WALink = getWhatsAppLink(
                        item.phone_number,
                        ''
                      );
                      window.open(WALink, '_blank');
                    }}><WhatsApp color={"primary"} fontSize='large' /></IconButton>
                    <IconButton  onClick={()=>window.location.href = `tel:${item.phone_number}`}><Phone color={"primary"} fontSize='large' /></IconButton>
                  </Box>
                </Box>}
              </Box>
            ))}
          </DialogContent>
        </CustomDialog>
      )}
    </CustomDialog>
  );
}

export default SendMessageDialog;
